"use client";

// Hand-built SVG donut (no chart lib): stroked arc paths, round caps, fixed
// 176×176 size. Round caps extend past a path's endpoints by (strokeWidth / 2),
// so the drawn gap between segments = desired VISUAL gap + 2 × cap angle —
// otherwise neighbouring caps would overlap. Hover thickens a segment and shows
// a simple overlay at its mid-angle.
//
// The ring is drawn in two layers: a grey TRACK for the whole, then the
// coloured segments on top of it. The track is what makes the centre figure
// legible — "8 jobs" with two 45° arcs says two of the eight are classified,
// whereas arcs that always fill the circle say only "these are the categories",
// and quietly imply the categories account for everything. The base grey is
// --muted-foreground/25, the same track value the tracker and the pipeline bars
// use: plain --muted sits 1.04:1 against --card and reads as an absent ring
// rather than as an unfilled one.

import { useEffect, useState } from "react";
import { DUR_SLOW, EASE_SETTLE_CSS } from "@/app/components/motion";

export interface ArcDonutDatum {
  label: string;
  value: number;
  color: string;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180; // 0° at 12 o'clock, clockwise
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

export function ArcDonut({
  data,
  centerValue,
  centerLabel,
  arcTotal,
}: {
  data: ArcDonutDatum[];
  centerValue: number;
  centerLabel: string;
  /**
   * The whole the arcs are a share of. Defaults to the sum of the segments —
   * a ring that is always full, for a caller whose categories are exhaustive.
   * The dashboard passes the total job count, so the categories that were never
   * classified show up as visible track instead of being inflated to 100%.
   */
  arcTotal?: number;
}) {
  const size = 176;
  const thickness = 15;
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - thickness) / 2;
  const capAngle = (Math.asin(thickness / 2 / r) * 180) / Math.PI;
  const drawnGap = 8 + capAngle * 2; // ≈8° of visible space between caps
  const segmentTotal = data.reduce((sum, item) => sum + item.value, 0);
  const total = arcTotal && arcTotal > segmentTotal ? arcTotal : segmentTotal;

  const [hovered, setHovered] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // Draw-in animation trigger, after hydration (timeout pattern).
    const timer = setTimeout(() => setMounted(true), 0);
    return () => clearTimeout(timer);
  }, []);

  const segments = data.map((item, index) => {
    const sweep = total > 0 ? (item.value / total) * 360 : 0;
    const valueBefore = data.slice(0, index).reduce((sum, d) => sum + d.value, 0);
    const startAngle = (valueBefore / total) * 360;
    const start = startAngle + drawnGap / 2;
    const end = startAngle + sweep - drawnGap / 2;
    return { ...item, start, end, mid: (start + end) / 2 };
  });
  const hoveredSegment = hovered != null ? segments[hovered] : null;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Category distribution">
        {/* The track: the whole ring, under the arcs. No dash animation — it
            is the baseline the arcs are measured against, not a value. */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={thickness}
          className="stroke-muted-foreground/25"
        />
        {segments.map((segment, index) =>
          segment.end - segment.start < 2 ? null : (
            <path
              key={segment.label}
              d={arcPath(cx, cy, r, segment.start, segment.end)}
              fill="none"
              stroke={segment.color}
              strokeWidth={hovered === index ? thickness + 2 : thickness}
              strokeLinecap="round"
              pathLength={100}
              strokeDasharray={100}
              strokeDashoffset={mounted ? 0 : 100}
              style={{
                // The ring draws itself over the slow token — the same 850ms
                // and the same expo-out the gauge and the bar fills use, so
                // every "this value is this big" gesture in the app is one
                // gesture. stroke-width keeps the hovering step at the fast
                // token, because that half is the pointer, not the data.
                transition: `stroke-dashoffset ${DUR_SLOW}s ${EASE_SETTLE_CSS}, stroke-width 150ms ease`,
                cursor: "pointer",
              }}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
            />
          )
        )}
      </svg>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold leading-none tracking-tight text-foreground tabular-nums">
          {centerValue}
        </span>
        <span className="mt-1 text-xs text-muted-foreground">{centerLabel}</span>
      </div>

      {hoveredSegment && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-md border border-border bg-card px-3 py-2 text-xs"
          style={polarToCartesian(cx, cy, r * 0.5, hoveredSegment.mid)}
        >
          <p className="font-medium text-foreground">
            {hoveredSegment.label}: {hoveredSegment.value}
          </p>
          <p className="text-muted-foreground">{Math.round((hoveredSegment.value / total) * 100)}%</p>
        </div>
      )}
    </div>
  );
}
